"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UnsafeCurrency = exports.Currency = exports.isSerializedStringFloatCurrency = exports.isSerializedCurrency = exports.CURRENCY_DECIMAL_PRECISION = exports.CURRENCY_CODE_NONE = exports.DEFAULT_CURRENCY_ROUNDING_MODE = void 0;
// eslint-disable-next-line @typescript-eslint/no-var-requires
const DineroFactory = require('dinero.js');
const currency_codes_1 = require("currency-codes");
const math_1 = require("./math");
const error_1 = require("./error");
const logger_1 = require("./logger");
exports.DEFAULT_CURRENCY_ROUNDING_MODE = 'HALF_EVEN';
// Convenience export for ISO-4217 currency code signifying "no currency".
exports.CURRENCY_CODE_NONE = 'XXX';
exports.CURRENCY_DECIMAL_PRECISION = {
    /**
     * Enforced minimum decimal precision for currency values.
     * Attempting to use lower precision will result in transparent adjustment to conform to minimum precision.
     *
     * 5-decimal precision is what was historically used for comparing if two bids are equivalent.
     */
    MINIMUM: 5,
    /**
     * Enforced maximum decimal precision for currency values.
     * Exceeding this precision will result in warning logs and automatic rounding.
     *
     * 7-decimal maximum precision is consistent with `Measurement`.
     * Minimum price fluctuation in US grain futures and options trading is 1/8 of one cent,
     * and max precision of 7 is also an arbitrary buffer past that.
     */
    MAXIMUM: 7
};
/** Typeguard checking if Currency input is already a Currency model instance */
const isCurrencyModel = (input) => {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return input instanceof Currency;
};
/** Typeguard checking if Currency input is a "safe" serialized currency type */
const isSerializedCurrency = (input) => {
    const maybeSerializedCurrency = input;
    return (Boolean(maybeSerializedCurrency) &&
        typeof maybeSerializedCurrency.amount === 'number' &&
        typeof maybeSerializedCurrency.precision === 'number' &&
        typeof maybeSerializedCurrency.currencyCode === 'string' &&
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        !(input instanceof Currency));
};
exports.isSerializedCurrency = isSerializedCurrency;
/** Typeguard checking if Currency input is a "stringfloat" serialized currency type */
const isSerializedStringFloatCurrency = (input) => {
    const maybeSerializedCurrency = input;
    return (Boolean(maybeSerializedCurrency) &&
        typeof maybeSerializedCurrency.value === 'string' &&
        typeof maybeSerializedCurrency.currencyCode === 'string' &&
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        !(input instanceof Currency));
};
exports.isSerializedStringFloatCurrency = isSerializedStringFloatCurrency;
const normalizeCurrencyCodeString = (currencyCode) => {
    if (!currencyCode || typeof currencyCode !== 'string') {
        // Note: Intentionally disallow empty string as currency code
        throw new error_1.CommonUtilsError('Currency tooling requires non-empty string value for `currencyCode`.', {
            currencyCode
        });
    }
    // Normalize the currency code to upper case regardless of the passed casing
    // to ensure consistency in Dinero currency comparisons,
    // since Dinero does case-sensitive comparisons
    // (would otherwise treat 'usd' and 'USD' as nonequal.)
    const normalized = currencyCode.toUpperCase();
    if (!currency_codes_1.code(currencyCode)) {
        throw new error_1.CommonUtilsError('Currency tooling requires valid ISO-4217 string value for `currencyCode`', {
            currencyCode
        });
    }
    return normalized;
};
class Currency {
    /**
     * Creates a `Currency` instance from an existing instance, or from a `SeriaalizedCurrency`.
     *
     * **CAUTION:** enforces a maximum (reasonable) decimal precision
     */
    constructor(input) {
        if (isCurrencyModel(input)) {
            // Assume that existing `Currency` models do not need:
            // * `currency` string validation
            // * `precision` integer or range validation
            this._dineroInstance = DineroFactory(
            // Clone the internal Dinero instance from the passed `Currency` instance.
            // Although Dinero instances are immutable, we still don't want
            // this new `Currency` to reference an existing one.
            input._dineroInstance.toObject());
        }
        else if (exports.isSerializedCurrency(input) || exports.isSerializedStringFloatCurrency(input)) {
            const safeFloat = exports.isSerializedStringFloatCurrency(input)
                ? new math_1.SmartFloat(input.value).toSafeFloat()
                : { amount: input.amount, precision: input.precision };
            // Intentionally disallow non-integer `precision`/`amount` values
            // Dinero validates these on its own, but throw `CommonUtilsError`s instead for consistency
            if (!Number.isInteger(safeFloat.precision) || safeFloat.precision < 0) {
                throw new error_1.CommonUtilsError('Currency constructor requires positive integer value for `precision`', { input });
            }
            if (!Number.isInteger(safeFloat.amount)) {
                throw new error_1.CommonUtilsError('Currency constructor requires integer value for `amount`', { input });
            }
            let normalized = Object.assign(Object.assign({}, safeFloat), { currencyCode: input.currencyCode });
            normalized = Currency._normalizeMaximumPrecision(normalized);
            normalized = Currency._normalizeMinimumPrecision(normalized);
            normalized.currencyCode = normalizeCurrencyCodeString(input.currencyCode);
            this._dineroInstance = DineroFactory({
                amount: normalized.amount,
                precision: normalized.precision,
                currency: normalized.currencyCode
            });
        }
        else {
            throw new error_1.CommonUtilsError('Unrecognized input format to Currency constructor', { input });
        }
    }
    // Detects whether an arbitrary arithmetic calculation results in an unsafe integer value,
    // which could cause precision loss due to integer under/overflow.
    // This function is meant to be a lightweight way to cause loud failures in the most likely places
    // for this issue to be encountered and therefore only protects conversions
    // done in the `Currency` constructor; intentionally does not protect any
    // of the Dinero-wrapping operations in this class.
    static _safeIntegerCalculate(context, calculate) {
        const result = calculate();
        if (!Number.isSafeInteger(result)) {
            throw new error_1.CommonUtilsError('Currency calculation encountered integer overflow error!', context);
        }
        return result;
    }
    // Internal helper; purposely not exposed in order to force usage of the `Currency` constructor.
    static _fromDinero(dinero) {
        return new Currency({
            amount: dinero.getAmount(),
            precision: dinero.getPrecision(),
            currencyCode: dinero.getCurrency()
        });
    }
    static _normalizeMinimumPrecision(currency) {
        // **NOTE! Any calculation involving the `amount` should be wrapped in `safeCalculate()`.**
        const context = {
            callsite: '_normalizeMinimumPrecision',
            input: currency
        };
        // Starting state
        let convertedPrecision = currency.precision;
        let convertedAmount = currency.amount;
        // If starting state does not conform to minimum precision, adjust accordingly
        if (convertedPrecision < exports.CURRENCY_DECIMAL_PRECISION.MINIMUM) {
            // No need to log a warning here; we're "inflating" to minimum precision
            // but are not losing any data/doing any rounding
            const padding = exports.CURRENCY_DECIMAL_PRECISION.MINIMUM - convertedPrecision;
            convertedPrecision += padding;
            // `convertedAmount` is guaranteed to be a whole number before this point; calculation should not require rounding
            convertedAmount = Currency._safeIntegerCalculate(context, () => convertedAmount * Math.pow(10, padding));
        }
        // DO NOT REMOVE - Redundant/no-op check to protect against any future refactoring
        convertedAmount = Currency._safeIntegerCalculate(context, () => convertedAmount);
        return {
            amount: convertedAmount,
            precision: convertedPrecision,
            currencyCode: currency.currencyCode
        };
    }
    static _normalizeMaximumPrecision(currency) {
        // **NOTE! Any calculation involving the `amount` should be wrapped in `safeCalculate()`.**
        const context = {
            callsite: '_normalizeMaximumPrecision',
            input: currency
        };
        // Starting state
        let convertedPrecision = currency.precision;
        let convertedAmount = currency.amount;
        // If starting state does not conform to maximum precision, adjust accordingly
        if (convertedPrecision > exports.CURRENCY_DECIMAL_PRECISION.MAXIMUM) {
            const padding = convertedPrecision - exports.CURRENCY_DECIMAL_PRECISION.MAXIMUM;
            convertedPrecision -= padding;
            const dinero = DineroFactory({
                amount: currency.amount,
                precision: currency.precision,
                currency: currency.currencyCode
            });
            const conversionResult = dinero.convertPrecision(convertedPrecision, exports.DEFAULT_CURRENCY_ROUNDING_MODE);
            const warningData = {
                originalAmount: currency.amount,
                originalPrecision: currency.precision,
                currencyCode: currency.currencyCode,
                enforcedMaximumDecimalPrecision: exports.CURRENCY_DECIMAL_PRECISION.MAXIMUM,
                roundedTo: conversionResult.toUnit()
            };
            logger_1.loggerWrapper.warn('WARNING: Currency module encountered data exceeding maximum allowed decimal precision.' +
                ` Rounding to default maximum precision. ${JSON.stringify(warningData)}`);
            convertedAmount = conversionResult.getAmount();
            convertedPrecision = conversionResult.getPrecision();
        }
        // DO NOT REMOVE - Redundant/no-op check to protect against any future refactoring
        convertedAmount = Currency._safeIntegerCalculate(context, () => convertedAmount);
        return {
            amount: convertedAmount,
            precision: convertedPrecision,
            currencyCode: currency.currencyCode
        };
    }
    // Low-effort way to trap internal errors from Dinero and rewrite them to reference `Currency` instead
    static _trapDineroError(dineroOperation) {
        try {
            return dineroOperation();
        }
        catch (e) {
            if (e.message === 'You must provide a Dinero instance with the same currency.') {
                throw new error_1.CommonUtilsError('You must provide a `Currency` instance with the same currencyCode.');
            }
            throw e;
        }
    }
    /**
     * @returns This `Currency` instance in "safe" serialized JSON form (safe precision handling)
     */
    serialize() {
        return {
            amount: this._dineroInstance.getAmount(),
            precision: this._dineroInstance.getPrecision(),
            currencyCode: this._dineroInstance.getCurrency()
        };
    }
    /**
     * @returns this Currency in "stringfloat" serialized JSON form (alternate safe float handling)
     */
    serializeWithStringFloat() {
        const _a = this.serialize(), { currencyCode } = _a, safeFloat = __rest(_a, ["currencyCode"]);
        const smartFloat = new math_1.SmartFloat(safeFloat);
        return {
            value: smartFloat.toStringFloat(),
            currencyCode
        };
    }
    /**
     * _**CAUTION!** Whenever possible, **AVOID "Unsafe" forms** and **prefer "Safe" forms** instead!_
     *
     * Official recommended pathway for converting a (safe) `Currency` to an `UnsafeCurrency`,
     * with primary (unfortunate) use case being when some API or library needs a float value
     * in straight `number` form. (Follow with `unsafeCurrency.serialize()` in this case.)
     *
     * `UnsafeCurrency` intentionally supports very little functionality compared to the normal/safe
     * `Currency` tooling. Recommended to only call this at the last minute before serialization to
     * unsafe float number format is needed.
     */
    convertToUnsafeCurrency() {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return new UnsafeCurrency(this._dineroInstance.toUnit(), // does not round
        this._dineroInstance.getCurrency());
    }
    /*
     * NOTE: All instance methods beyond this line wrap native Dinero methods, but this class
     * is not meant to be perfectly 1:1 API compatible with Dinero, primarily for safety/clarity/
     * separation of presentation concerns from this class.
     *
     * If this class is not currently exposing some desired underlying Dinero functionality,
     * think twice about exposing additional wrapping methods here or in related presentation tooling.
     */
    // ---
    /*
     * NOTE: The following methods are intentionally private since they overlap or interfere
     * with conventions used in the other classes exported from this file.
     * Explicitly defining them as private intentionally causes any future attempts
     * to add public methods with colliding names to result in compilation errors.
     */
    // Before serialization, `amount`/`currencyCode`/`precision` should never need to be inspected directly/
    // should always be able to be manipulated by the other public methods of this class.
    /** @deprecated Use `currencyInstance.serialize().amount` instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    getAmount() {
        throw new error_1.CommonUtilsError('`getAmount` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Use `currencyInstance.serialize().currencyCode` instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    getCurrencyCode() {
        throw new error_1.CommonUtilsError('`getCurrencyCode` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Alias of `getCurrencyCode`; use `currencyInstance.serialize().currencyCode` instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    getCurrency() {
        throw new error_1.CommonUtilsError('`getCurrency` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Use `currencyInstance.serialize().getPrecision` instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    getPrecision() {
        throw new error_1.CommonUtilsError('`getPrecision` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Use `currencyInstance.convertToUnsafeCurrency().serialize().value` instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    toRoundedUnit(digits, roundingMode = exports.DEFAULT_CURRENCY_ROUNDING_MODE) {
        throw new error_1.CommonUtilsError('`toRoundedUnit` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Use `currencyInstance.convertToUnsafeCurrency().serialize().value` instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    toUnit() {
        throw new error_1.CommonUtilsError('`toUnit` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Use `currencyInstance.divide()` or `currencyInstance.multiply()` instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    percentage() {
        throw new error_1.CommonUtilsError('`percentage` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Dinero-deprecated version of hasSubUnits()
     * @throws EXPLICITLY UNSUPPORTED
     */
    hasCents() {
        throw new error_1.CommonUtilsError('`hasCents` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Work with precision directly instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    hasSubUnits() {
        throw new error_1.CommonUtilsError('`hasSubUnits` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated `Currency`-specific alias of hasSubUnits(). Work with precision directly instead
     * @throws EXPLICITLY UNSUPPORTED
     */
    hasMinorUnits() {
        throw new error_1.CommonUtilsError('`hasMinorUnits` is explicitly unsupported by `Currency`!');
    }
    /** @deprecated Use `currencyInstance.equalsTo()` or `currencyInstance.isZero()` instead,
     *              to enforce comparisons that honor `currencyCode`. If you absolutely must
     *              compare amounts directly, you can compare `SerializedCurrency` amounts
     *              after calling `currencyInstance.serialize()` on both.
     * @throws EXPLICITLY UNSUPPORTED
     */
    hasSameAmount(comparator) {
        // return this._dineroInstance.hasSameAmount(new Currency(comparator)._dineroInstance);
        throw new error_1.CommonUtilsError('`hasSameAmount` is explicitly unsupported by `Currency`!');
    }
    /**
     * Not exposed for the time being, until we decide on a
     * more holitic/intentional strategy for currency conversion.
     * Note: `ExchangeRatesApiOptions` should be re-exported from this file
     * if this method becomes publicly exposed.
     *
     * @throws EXPLICITLY UNSUPPORTED
     */
    async convert(currencyType, options) {
        /*
        const converted = await this._dineroInstance.convert(currencyType, options);
        return Currency._fromDinero(converted);
        */
        throw new error_1.CommonUtilsError('`convert` is explicitly unsupported by `Currency`!');
    }
    // ^^^ End explicitly-unsupported private methods ^^^
    /*
     * NOTE: The following formatting-related methods are intentionally private
     * because this class is presently not meant to handle formatting/presentation concerns.
     *
     * This may change in the future, but for now, explicitly defining them as private
     * intentionally causes any future attempts to add public methods with colliding names
     * to result in compilation errors.
     */
    /**
     * @throws EXPLICITLY UNSUPPORTED
     */
    setLocale(newLocale) {
        throw new error_1.CommonUtilsError('`setLocale` is explicitly unsupported by `Currency`!');
    }
    /**
     * @throws EXPLICITLY UNSUPPORTED
     */
    getLocale() {
        throw new error_1.CommonUtilsError('`getLocale` is explicitly unsupported by `Currency`!');
    }
    /**
     * @throws EXPLICITLY UNSUPPORTED
     */
    toFormat() {
        throw new error_1.CommonUtilsError('`toFormat` is explicitly unsupported by `Currency`!');
    }
    // ^^^ End formatting-related private methods ^^^
    /**
     * @returns true if this `Currency` has a negative amount, false otherwise
     */
    isNegative() {
        return this._dineroInstance.isNegative();
    }
    /**
     * @returns true if this `Currency` has a positive amount, false otherwise
     */
    isPositive() {
        return this._dineroInstance.isPositive();
    }
    /**
     * @returns true if this `Currency` has an amount equal to 0, false otherwise
     */
    isZero() {
        return this._dineroInstance.isZero();
    }
    /**
     * @param newPrecision Positive integer value representing a new precision to convert to
     * @param roundingMode `CurrencyRoundingMode` representing which rounding mode to use
     * @throws `CommonUtilsError` if `newPrecision` is outside allowed range
     * @returns A new `Currency` instance converted to the requested precision
     */
    toPrecision(newPrecision, roundingMode = exports.DEFAULT_CURRENCY_ROUNDING_MODE) {
        const errorData = {
            existingCurrency: this.serialize(),
            requestedPrecision: newPrecision,
            allowedPrecision: exports.CURRENCY_DECIMAL_PRECISION
        };
        if (!Number.isInteger(newPrecision) || newPrecision < 0) {
            throw new error_1.CommonUtilsError('Currency.toPrecision(): Requested precision must be a positive integer value', errorData);
        }
        // If a precision was requested that falls outside the allowed range, explicitly throw
        // to inform the caller, rather than allowing precision inflation/rounding to
        // implicitly/silently happen inside the `Currency()` constructor.
        if (newPrecision > exports.CURRENCY_DECIMAL_PRECISION.MAXIMUM) {
            throw new error_1.CommonUtilsError('Currency.toPrecision(): Requested precision was higher than maximum allowed precision', errorData);
        }
        else if (newPrecision < exports.CURRENCY_DECIMAL_PRECISION.MINIMUM) {
            throw new error_1.CommonUtilsError('Currency.toPrecision(): Requested precision was lower than minimum allowed precision', errorData);
        }
        return Currency._fromDinero(this._dineroInstance.convertPrecision(newPrecision, roundingMode));
    }
    /**
     * @param addends `CurrencyInput`s to add together
     * @returns A new `Currency` instance representing the sum of
     *          the amounts of all passed addends
     * @throws `CommonUtilsError` if passed addends were not valid `CurrencyInput`s or could not be added
     */
    static sum(addends) {
        if (addends.length === 0) {
            throw new error_1.CommonUtilsError('Currency.sum(): `addends` cannot be empty');
        }
        let validatedAddends;
        try {
            // Validate by explicitly passing all addends to the `Currency` constructor
            // (also normalizes all `CurrencyInput`s to `Currency` instances)
            validatedAddends = addends.map(addend => new Currency(addend));
        }
        catch (error) {
            throw new error_1.CommonUtilsError('Currency.sum(): One or more passed `addends` was invalid', {
                addends: addends.map(addend => (addend instanceof Currency ? addend.serialize() : addend))
            });
        }
        // Assume all addends have the same `currencyCode` and use the first one to initialize the empty Dinero instance;
        // subsequent addition operations will validate that they are indeed all identical
        let result = DineroFactory({ currency: validatedAddends[0].serialize().currencyCode, amount: 0 });
        for (const validatedAddend of validatedAddends) {
            result = Currency._trapDineroError(() => result.add(validatedAddend._dineroInstance));
        }
        return Currency._fromDinero(result);
    }
    /**
     * @param addends `CurrencyInput`s to add to the amount represented by this `Currency` instance
     * @returns A new `Currency` instance representing the amounts of all passed addends added to
     *          the amount from this `Currency` instance
     * @throws `CommonUtilsError` if passed addends were not valid `CurrencyInput`s or could not be added
     */
    add(...addends) {
        let newAmount = this._dineroInstance;
        for (const addend of addends) {
            newAmount = Currency._trapDineroError(() => newAmount.add(new Currency(addend)._dineroInstance));
        }
        return Currency._fromDinero(newAmount);
    }
    /**
     * @param subtrahends `CurrencyInput`s to subtract from the amount represented by this `Currency` instance
     * @returns A new `Currency` instance representing the amounts of all passed subtrahends subtracted from
     *          the amount from this `Currency` instance
     * @throws `CommonUtilsError` if passed subtrahends were not valid `CurrencyInput`s or could not be substracted
     */
    subtract(...subtrahends) {
        let newAmount = this._dineroInstance;
        for (const subtrahend of subtrahends) {
            newAmount = Currency._trapDineroError(() => newAmount.subtract(new Currency(subtrahend)._dineroInstance));
        }
        return Currency._fromDinero(newAmount);
    }
    /**
     * @param multiplier `CurrencyInput` to multiply by the amount represented by this `Currency` instance
     * @returns A new `Currency` instance representing the amount of this
     *          `Currency` instance multipled by the passed multiplier
     * @throws `CommonUtilsError` if passed multiplier is not a valid `CurrencyInput`
     */
    multiply(multiplier
    /*
    // `roundingMode` is currently not exposed for safety, can expose later if necessary
    roundingMode: CurrencyRoundingMode = DEFAULT_CURRENCY_ROUNDING_MODE
    */
    ) {
        const parsed = new Currency(multiplier);
        // `toUnit()` is used internally since Dinero requires the multiplier to be a float value
        return Currency._fromDinero(this._dineroInstance.multiply(parsed._dineroInstance.toUnit(), exports.DEFAULT_CURRENCY_ROUNDING_MODE));
    }
    /**
     * @param divisor `CurrencyInput` to divide into the amount represented by this `Currency` instance
     * @returns A new `Currency` instance representing the amount of this
     *          `Currency` instance divided by the passed divisor
     * @throws `CommonUtilsError` if passed divisor is not a valid `CurrencyInput`
     */
    divide(divisor
    /*
    // `roundingMode` is currently not exposed for safety, can expose later if necessary
    roundingMode: CurrencyRoundingMode = DEFAULT_CURRENCY_ROUNDING_MODE
    */
    ) {
        const parsed = new Currency(divisor);
        // `toUnit()` is used internally since Dinero requires the divisor to be a float value
        return Currency._fromDinero(this._dineroInstance.divide(parsed._dineroInstance.toUnit(), exports.DEFAULT_CURRENCY_ROUNDING_MODE));
    }
    /**
     * Allocates the amount of a `Currency` object according to a list of ratios.
     *
     * Sometimes you need to split monetary values but percentages can't cut it without adding or losing pennies.
     * A good example is invoicing: let's say you need to bill $1,000.03 and you want a 50% downpayment.
     * If you use percentage, you'll get an accurate `Currency` object but the amount won't be billable:
     * you can't split a penny. If you round it, you'll bill a penny extra.
     *
     * With allocate, you can split a monetary amount then distribute the remainder as evenly as possible.
     *
     * You can use percentage style or ratio style for ratios: [25, 75] and [1, 3] will do the same thing.
     *
     * @param ratios The ratios to allocate the money to/the percentages to extract (between 0 and 100).
     * @returns An array of `Currency` instances corresponding to the passed allocations
     */
    allocate(ratios) {
        return this._dineroInstance.allocate(ratios).map(x => Currency._fromDinero(x));
    }
    /**
     * @param comparator `CurrencyInput` to compare to this `Currency` instance
     * @returns true if this `Currency` is equal to that of the passed `Currency`, false otherwise
     * @throws `CommonUtilsError` if passed `comparator` is not a valid `CurrencyInput`
     */
    equalsTo(comparator) {
        return this._dineroInstance.equalsTo(new Currency(comparator)._dineroInstance);
    }
    /**
     * @param comparator `CurrencyInput` to compare to this `Currency` instance
     * @returns true if this `Currency`'s amount is less than that of the passed `Currency`, false otherwise
     * @throws `CommonUtilsError` if `comparator` is not a valid `CurrencyInput`
     *         or if `currencyCode`s between this `Currency` and passed `comparator` do not match
     */
    lessThan(comparator) {
        return Currency._trapDineroError(() => this._dineroInstance.lessThan(new Currency(comparator)._dineroInstance));
    }
    /**
     * @param comparator `CurrencyInput` to compare to this `Currency` instance
     * @returns true if this `Currency`'s amount is less than or equal to that of the passed `Currency`, false otherwise
     * @throws `CommonUtilsError` if `comparator` is not a valid `CurrencyInput`
     *         or if `currencyCode`s between this `Currency` and passed `comparator` do not match
     */
    lessThanOrEqual(comparator) {
        return Currency._trapDineroError(() => this._dineroInstance.lessThanOrEqual(new Currency(comparator)._dineroInstance));
    }
    /**
     * @param comparator `CurrencyInput` to compare to this `Currency` instance
     * @returns true if this `Currency`'s amount is greater than that of the passed `Currency`, false otherwise
     * @throws `CommonUtilsError` if `comparator` is not a valid `CurrencyInput`
     *         or if `currencyCode`s between this `Currency` and passed `comparator` do not match
     */
    greaterThan(comparator) {
        return Currency._trapDineroError(() => this._dineroInstance.greaterThan(new Currency(comparator)._dineroInstance));
    }
    /**
     * @param comparator `CurrencyInput` to compare to this `Currency` instance
     * @returns true if this `Currency`'s amount is greater than or equal to that of the passed `Currency`, false otherwise
     * @throws `CommonUtilsError` if `comparator` is not a valid `CurrencyInput`
     *         or if `currencyCode`s between this `Currency` and passed `comparator` do not match
     */
    greaterThanOrEqual(comparator) {
        return Currency._trapDineroError(() => this._dineroInstance.greaterThanOrEqual(new Currency(comparator)._dineroInstance));
    }
    /**
     * @param comparator `CurrencyInput` to compare to this `Currency` instance
     * @returns true if this `Currency` has the same currency code as the passed `Currency`, false otherwise
     * @throws `CommonUtilsError` if `comparator` is not a valid `CurrencyInput`
     */
    hasSameCurrencyCode(comparator) {
        return this._dineroInstance.hasSameCurrency(new Currency(comparator)._dineroInstance);
    }
    /**
     * _**CAUTION!** Whenever possible, the standard SerializedCurrency form is strongly preferred
     * to non-universal, less-flexible legacy forms._
     *
     * Special build method for constructing directly legacy "SafeCurrency" form in Marketplace GraphQL APIs.
     * */
    static fromLegacySafeCurrency(safeCurrency) {
        const { amount, precision, currency } = safeCurrency;
        return new Currency({ amount, precision, currencyCode: currency });
    }
    /**
     * _**CAUTION!** Whenever possible, the standard SerializedCurrency form is strongly preferred
     * to non-universal, less-flexible legacy forms._
     *
     * Special serialize method for constructing directly to legacy "SafeCurrency" form in Marketplace GraphQL APIs.
     *
     * NOTE: consumer may have to cast the type since this tooling has no knowledge of the GraphQL CurrencyCode enum
     * and has to represent the unit as a primitive string type.
     */
    static toLegacySafeCurrency(currency) {
        const { amount, precision, currencyCode } = currency.serialize();
        return { amount, precision, currency: currencyCode };
    }
}
exports.Currency = Currency;
/**
 * _**CAUTION!** Whenever possible, **AVOID "Unsafe" forms** and **prefer "Safe" forms** instead!_
 * @see `Currency` for "safe" alternative
 *
 * Official utility class for handling any *unsafe* "currency" values (where "currency" means
 * an amount with arbitrary precision that must be paired with a currency code.)
 *
 * The `UnsafeCurrency` class is intentionally minimal in order to
 * deliberately discourage use whenever possible.
 * It is **strongly recommended** to convert instances of `UnsafeCurrency`
 * to the safe `Currency` alternative.
 *
 * Valid use cases for the `UnsafeCurrency` class revolve around communication with dependencies
 * that cannot be easily modified to use "safe" serialized forms:
 * - ingesting an "unsafe" float number value from external some data source or API, with intent
 *   to immediately convert to safe `Currency` (**strongly recommended**)
 *   - see `unsafeCurrency.convertToCurrency()`
 *   - also see `UnsafeCurrency.fromMinorUnits(...)` for ingesting values in cents/etc.
 * - as an intermediate form in converting a safe `Currency` to an unsafe serialized form when a
 *   straight float `number` is required by some dependency (e.g. downstream API)
 *   - see `currency.convertToUnsafeCurrency()` and `unsafeCurrency.serialize()`
 * */
class UnsafeCurrency {
    /**
     * _**CAUTION!** Whenever possible, **AVOID "Unsafe" forms** and **prefer "Safe" forms** instead!_
     *
     * Creates an `UnsafeCurrency` instance from "unsafe" serialized currency data.
     *
     * Main purpose: vehicle to convert "unsafe" float number data to a "safe" `Currency` instance
     * (**strongly recommended to do as soon as possible**).
     *
     * NOTE: This constructor does not validate precision nor integer safety,
     * intentionally allowing for potentially unsafe manipulations within `UnsafeCurrency`
     * before calling `convertToCurrency()`.
     */
    constructor(value, currencyCode) {
        this._value = new math_1.SmartFloat(value);
        this._currencyCode = normalizeCurrencyCodeString(currencyCode);
    }
    /**
     * _**CAUTION!** Whenever possible, **AVOID "Unsafe" forms** and **prefer "Safe" forms** instead!_
     * @see `SerializedCurrency` for "safe" alternative
     *
     * Official recommended pathway for extracting a float value (and currency code) from deserialized
     * `UnsafeCurrency` form.
     * - Use in combination with `currency.convertToUnsafeCurrency()` if you need to convert a safe
     *   `Currency` instance to a straight float value `number`.
     *
     * @returns This `UnsafeCurrency` instance in serialized JSON form
     */
    serialize() {
        return {
            value: this._value.toNumber(),
            currencyCode: this._currencyCode
        };
    }
    /**
     * **STRONGLY RECOMMENDED TO CALL THIS AS EARLY AS POSSIBLE AFTER CONSTRUCTION.**
     *
     * The "safe" Currency class has lots of useful tooling for arithmetic, comparision, precision, etc.
     *
     * Caveat: "safe" Currency will involve conversion and can sometimes change the underlying data
     * (e.g. if the data runs afoul of the minimum reasonable decimal precision).
     *
     * @returns A safe `Currency` instance representation of this `UnsafeCurrency`
     * @throws `CommonUtilsError` if this `UnsafeCurrency` could not be successfully converted to a `Currency` instance
     */
    convertToCurrency() {
        const { amount, precision } = this._value.toSafeFloat();
        // Implicitly performs further validation
        return new Currency({ amount, precision, currencyCode: this._currencyCode });
    }
    /**
     * Helper for `fromMinorUnits` (computes a value for its `minorUnitPrecision` parameter.)
     * Returns a positive integer value indicating the standard number of digits that appear
     * after the decimal place when working in whole units of an input `CurrencyCode` (minor unit precision.)
     * -- e.g. for USD cents (the "minor unit" for USD), this will return 2
     * -- see https://en.wikipedia.org/wiki/ISO_4217#Minor_units_of_currency for info on minor units in other currencies
     *
     * @param input `CurrencyCode`, or `CurrencyInput` from which to read a currency code.
     * @returns Positive integer precision value.
     * @throws `CommonUtilsError` if `input` is either invalid or represents a currency that does not support minor units
     * @see fromMinorUnits
     */
    static minorUnitPrecisionForCurrencyCode(input) {
        const currencyCode = typeof input === 'string' ? normalizeCurrencyCodeString(input) : new Currency(input).serialize().currencyCode;
        const currencyCodeData = currency_codes_1.code(currencyCode);
        // Assume that unknown code or 0 decimal digits means that minor units are not supported
        if (!currencyCodeData || !currencyCodeData.digits) {
            throw new error_1.CommonUtilsError('UnsafeCurrency.minorUnitPrecisionForCurrencyCode(): Supplied input is either invalid' +
                ' or represents a currency that does not support minor units', {
                input: typeof input === 'string' ? input : new Currency(input).serialize()
            });
        }
        return currencyCodeData.digits;
    }
    /**
     * Mechanism for deserializing from "minor units" (e.g. USD cents in float value, commonly used for futures references prices)
     * - see https://en.wikipedia.org/wiki/ISO_4217#Minor_units_of_currency for minor units in different currencies.
     *
     * This concept is represented in `UnsafeCurrency` rather than in `Currency` as
     * a first-class citizen, since float values are inherently unsafe.
     *
     * To indirectly create a `Currency` model from minor units, use this method thusly:
     *
     *   const minorUnitPrecision = UnsafeCurrency.minorUnitPrecisionForCurrencyCode('USD') // can also pass a `CurrencyInput`
     *   const currency = UnsafeCurrency.fromMinorUnits(123.456, 'USD', minorUnitPrecison).convertToCurrency()
     *
     * NOTE: Although `minorUnitPrecision` could be calculated implicitly from the passed `CurrencyCode`,
     * developers are required to explicitly pass a `minorUnitPrecision` to explicitly declare
     * that they are aware of the precision of the passed arguments.
     *
     * NOTE: This method does not validate precision range nor integer safety, intentionally
     * allowing for potentially unsafe manipulations within `UnsafeCurrency` before
     * calling `convertToCurrency()`.
     *
     * @param value Float amount, in minor units (for `USD`, 1.45 would mean 1.45 cents, or $0.0145)
     * @param currencyCode Currency code in ISO-4217 format (https://en.wikipedia.org/wiki/ISO_4217)
     * @param minorUnitPrecision Positive integer value indicating number of digits after the decimal place
     *                           of whole units of `currencyCode` that correspond to these minor units;
     *                           will cause `value` to be divided by `Math.pow(10, minorUnitPrecision)`
     * @returns A safe `Currency` instance representation of the passed parameters
     * @throws `CommonUtilsError` if a `Currency` instance could not be successfully created
     * @see minorUnitPrecisionForCurrencyCode
     */
    static fromMinorUnits(value, currencyCode, minorUnitPrecision) {
        const errorData = {
            value,
            currencyCode,
            minorUnitPrecision
        };
        const normalizedCurrencyCode = normalizeCurrencyCodeString(currencyCode);
        const currencyCodeData = currency_codes_1.code(normalizedCurrencyCode);
        if (!currencyCodeData) {
            throw new error_1.CommonUtilsError('UnsafeCurrency.fromMinorUnits(): Supplied currency code is invalid', errorData);
        }
        if (!Number.isInteger(minorUnitPrecision) || minorUnitPrecision < 0) {
            throw new error_1.CommonUtilsError('UnsafeCurrency.fromMinorUnits(): Supplied `minorUnitPrecision` value must be positive a integer value', errorData);
        }
        if (!currencyCodeData.digits || currencyCodeData.digits !== minorUnitPrecision) {
            logger_1.loggerWrapper.warn('UnsafeCurrency.fromMinorUnits(): Supplied `minorUnitPrecision` value conflicts with known precision for supplied currency code: ' +
                JSON.stringify(errorData, null, 2));
        }
        return new UnsafeCurrency(new math_1.SmartFloat(value).divide(Math.pow(10, minorUnitPrecision)).toNumber(), normalizedCurrencyCode);
    }
}
exports.UnsafeCurrency = UnsafeCurrency;
//# sourceMappingURL=currency.js.map